import {
  ParamsCreateReport,
  ParamsUpdateApproveReport,
  ParamsUpdateCancelReport,
  ParamsUpdateEditReport,
  ParamsUpdateSubmitReport,
  ResponseApprover,
  ResponseGetReports,
  getApprover,
  getReports,
  updateApproveReport,
  updateCancelReport,
  updateEditReport,
  updateSubmitReport,
} from './../apis/report.api';

import {
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  conditionObj,
  getArrayVal,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import { getDetailInstitution } from '../apis/institution.api';
import { Order, ResponseGetOrderWorklist, getOrders } from '../apis/order.api';
import { createReport } from '../apis/report.api';
import { ResponseFetchStudyList } from '../apis/study.api';
import { SORT_ORDER } from '../constants/filter.constant';
import { queryKey } from '../constants/queryClientKey';
import { Institution } from '../interfaces/index';

export function useGetReports(
  params: Parameters<typeof getReports>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.reports, sortObjectKeys(params)],
    () => getReports(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getReports>>>;
}

export function useGetApprover(
  params: Parameters<typeof getApprover>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.reports, sortObjectKeys(params)],
    () => getApprover(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getApprover>>>;
}

export function useGetListReport(
  selectedRowKeys:
    | ResponseFetchStudyList['data']
    | ResponseGetOrderWorklist['data'],
  options?: UseQueryOptions
) {
  return useQueries({
    queries: selectedRowKeys.map(
      (row: typeof selectedRowKeys[number], index) => ({
        queryKey: [queryKey.orders, row?.study_id],
        queryFn: async () => {
          const param = {
            ...conditionObj('order_group', row?.order_group),
            ...conditionObj('patient_id', row?.patient_id),
            ...conditionObj('patient_name', row?.patient_name),
            ...conditionObj('study_id', row?.study_id),
            ...conditionObj('visit_id', row?.visit_id),
          };

          const recentOrder = await getOrders({
            ...param,
            offset: 0,
            limit: 1,
          }).then((res) => getArrayVal(res?.data));

          if (!recentOrder) {
            throw new Error(`Recent order not found. Response: ${recentOrder}`);
          }

          const recentReportPromise = getReports({
            offset: 0,
            limit: 1,
            order_id: recentOrder?.id,
            institution_ids: [recentOrder?.institution_id],
            sort_order: SORT_ORDER.DESC,
            sort_field: 'updated_at',
          });
          const institutionDetailPromise = getDetailInstitution(
            recentOrder?.institution_id
          );

          const recentReport = await recentReportPromise.then(
            (x) => x?.data?.[0]
          );
          const institutionDetail = await institutionDetailPromise.then(
            (res) => res?.data
          );

          const { data: approver } = await getApprover({
            id: recentReport?.id,
          });

          return {
            recentOrder,
            recentReport,
            study: selectedRowKeys[index],
            approver,
            institutionDetail,
          };
        },
        ...options,
      })
    ),
  }) as UseQueryResult<{
    recentOrder: Order;
    recentReport: ResponseGetReports['data'][number];
    study: ResponseFetchStudyList['data'][number];
    institutionDetail: Institution;
    approver: ResponseApprover['data'];
  }>[];
}

export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateReport) => createReport(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },
      onError: (err) => {
        console.error('useCreateReport:', err);
      },
    }
  );
}

export function useUpdateApproveReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateApproveReport }) =>
      updateApproveReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },

      onError: (err) => {
        console.error('useUpdateApproveReport:', err);
      },
    }
  );
}

export function useUpdateEditReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data?: ParamsUpdateEditReport }) =>
      updateEditReport(id, data),
    {
      onSuccess: () => {
        console.log('editing');
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },

      onError: (err) => {
        console.error('useUpdateEditReport:', err);
      },
    }
  );
}

export function useUpdateSubmitReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateSubmitReport }) =>
      updateSubmitReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },

      onError: (err) => {
        console.error('useUpdateSubmitReport:', err);
      },
    }
  );
}

export function useUpdateCancelReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data?: ParamsUpdateCancelReport }) =>
      updateCancelReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },

      onError: (err) => {
        console.error('useUpdateSubmitReport:', err);
      },
    }
  );
}
